import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { Container, Box, Typography, Button, CircularProgress } from "@mui/material";
import { BsArrowLeft } from "react-icons/bs";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";

export default function EmployeeRouteMap() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useSelector((state) => state?.loginState?.auth);

  const [loading, setLoading] = useState(true);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [employeeName, setEmployeeName] = useState("");

  const getCurrentFinancialYear = () => {
    return new Date().getFullYear().toString();
  };

  const getFinancialYears = () => {
    const years = [];
    const currentYear = new Date().getFullYear();
    for (let y = 2022; y <= currentYear + 1; y++) {
        years.push(y.toString());
    }
    return years;
  };

  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [month, setMonth] = useState("");
  const [taskStatus, setTaskStatus] = useState("");

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return { bg: "#0da62eff", border: "#0b8a25" };
      case 'in_progress': return { bg: "#a1a1e6ff", border: "#8c8ce0" };
      case 'pending': return { bg: "#92c3e3ff", border: "#7baecf" };
      case 'review': return { bg: "#57f6deff", border: "#46d5c0" };
      case 'approved': return { bg: "#179a17ff", border: "#148514" };
      case 'blocked': return { bg: "#bb0b0bff", border: "#9f0909" };
      case 'rejected': return { bg: "#e96d6dff", border: "#d05f5f" };
      default: return { bg: "#94a3b8", border: "#64748b" }; // gray
    }
  };

  useEffect(() => {
    const fetchEmployeeTasks = async () => {
      setLoading(true);
      try {
        let filterObj = { "employee_id": parseInt(id) };
        if (financialYear) filterObj["work__financial_year"] = financialYear;
        if (month) filterObj["work__created_at__month"] = parseInt(month);

        const assignmentsResponse = await axios.get(`${process.env.REACT_APP_URL || 'http://127.0.0.1:8000/api/'}work-assign/0/?filter_by=${JSON.stringify(filterObj)}&limit=200`, {
          headers: { Authorization: `Bearer ${auth?.token}` }
        });
        
        let assignmentData = assignmentsResponse.data?.data || assignmentsResponse.data;
        let assignments = assignmentData?.results || assignmentData;
        if (!Array.isArray(assignments)) assignments = [];

        // Flatten tasks from assignments
        let allTasks = [];
        assignments.forEach(assignment => {
            let tasks = assignment.tasks || [];
            if (taskStatus) {
                tasks = tasks.filter(t => t.status === taskStatus);
            }
            tasks.forEach(t => {
                t.work = assignment.work;
                t.work_name = assignment.work_name;
                t.assigned_to = assignment.employee_name;
                t.assigned_date = assignment.start_date;
                allTasks.push(t);
            });
        });

        let tasks = allTasks;

        // Determine employee name from assignments
        let eName = "Employee";
        if (assignments.length > 0 && assignments[0].employee_name) {
          eName = assignments[0].employee_name;
        } else if (tasks.length > 0 && tasks[0].assigned_to) {
          eName = tasks[0].assigned_to;
        }
        setEmployeeName(eName);

        if (tasks.length === 0) {
           setNodes([]);
           setEdges([]);
           setLoading(false);
           return;
        }

        // --- TIME-BASED LAYOUT CALCULATIONS ---
        
        let minTime = Infinity;
        let maxTime = -Infinity;

        // Ensure "Today" is always within the timeline view, since incomplete tasks will be placed here
        const todayTime = new Date().getTime();
        minTime = todayTime;
        maxTime = todayTime;

        tasks.forEach(t => {
           let times = [
             new Date(t.assigned_date).getTime(),
             new Date(t.started_date).getTime(),
             new Date(t.due_date).getTime(),
             new Date(t.completed_at).getTime()
           ].filter(val => !isNaN(val) && val > 0);
           
           if (times.length > 0) {
              const taskMin = Math.min(...times);
              const taskMax = Math.max(...times);
              if (taskMin < minTime) minTime = taskMin;
              if (taskMax > maxTime) maxTime = taskMax;
           }
        });

        // Fallback if no valid dates
        if (minTime === Infinity) minTime = new Date().getTime();
        if (maxTime === -Infinity) maxTime = new Date().getTime();

        const ONE_DAY = 24 * 60 * 60 * 1000;
        // Add padding: 15 days before, 30 days after
        minTime -= 15 * ONE_DAY;
        maxTime += 30 * ONE_DAY;

        const pixelsPerDay = 30; // Horizontal scale
        const dateToX = (dateStr) => {
            if (!dateStr) return 0;
            const t = new Date(dateStr).getTime();
            if (isNaN(t)) return 0;
            return ((t - minTime) / ONE_DAY) * pixelsPerDay + 600; // 600 offset for Client & Work nodes
        };

        const initialNodes = [];
        const initialEdges = [];

        // 1. Generate Vertical Grid Lines (Weekly)
        let gridM = new Date(minTime);
        // Snap to the closest Monday
        let day = gridM.getDay();
        let diff = gridM.getDate() - day + (day === 0 ? -6 : 1);
        gridM.setDate(diff);

        while (gridM.getTime() <= maxTime) {
            let x = dateToX(gridM);
            initialNodes.push({
               id: `grid-weekly-${gridM.getTime()}`,
               position: { x: x, y: -50 }, // Start slightly above the tasks
               data: { label: gridM.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) },
               style: {
                   width: 150,
                   // height will be set dynamically below
                   background: 'transparent',
                   borderLeft: '2px dashed #94a3b8',
                   borderTop: 'none', borderRight: 'none', borderBottom: 'none',
                   zIndex: -1,
                   fontSize: '12px',
                   color: '#64748b',
                   padding: '10px 0 0 10px',
                   fontWeight: 'bold',
                   textAlign: 'left',
                   pointerEvents: 'none'
               },
               draggable: false,
               selectable: false
            });
            gridM.setDate(gridM.getDate() + 7);
        }

        // 1.5 Generate Red Grid Lines for Deadlines
        const uniqueDeadlines = new Set();
        tasks.forEach(t => {
            if (t.due_date) {
                const dDate = new Date(t.due_date);
                if (!isNaN(dDate.getTime())) {
                    // Set to start of day to avoid duplicate lines for the same day
                    dDate.setHours(0,0,0,0);
                    uniqueDeadlines.add(dDate.getTime());
                }
            }
        });

        uniqueDeadlines.forEach(time => {
            let x = dateToX(new Date(time));
            initialNodes.push({
               id: `grid-deadline-${time}`,
               position: { x: x, y: -50 },
               data: { label: 'Deadline: ' + new Date(time).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) },
               style: {
                   width: 150,
                   background: 'transparent',
                   borderLeft: '2px dashed #ef4444', // Red dashed line
                   borderTop: 'none', borderRight: 'none', borderBottom: 'none',
                   zIndex: -1,
                   fontSize: '11px',
                   color: '#ef4444',
                   padding: '24px 0 0 4px', // Push label down a bit to avoid overlapping weekly labels
                   fontWeight: 'bold',
                   textAlign: 'left',
                   pointerEvents: 'none'
               },
               draggable: false,
               selectable: false
            });
        });

        // 2. Build Hierarchy: Client -> Work -> Task
        const hierarchy = {};
        tasks.forEach(t => {
           if (!hierarchy[t.client_name]) {
               hierarchy[t.client_name] = { works: {} };
           }
           if (!hierarchy[t.client_name].works[t.work]) {
               hierarchy[t.client_name].works[t.work] = {
                   workName: t.work_name || `Work #${t.work}`,
                   tasks: []
               };
           }
           hierarchy[t.client_name].works[t.work].tasks.push(t);
        });

        let currentY = 0;
        const ROW_HEIGHT = 220; // Task node height + margin

        // 3. Layout Nodes
        Object.keys(hierarchy).forEach((clientName, cIndex) => {
           const cNodeId = `client-${cIndex}`;
           let clientStartY = currentY;
           let clientMinX = Infinity;
           
           const works = hierarchy[clientName].works;
           Object.keys(works).forEach(workId => {
              const work = works[workId];
              const wNodeId = `work-${workId}`;
              let workStartY = currentY;
              let workMinX = Infinity;

              // Sort tasks chronologically by assigned_date so they stack vertically in order
              const sortedTasks = work.tasks.sort((a,b) => new Date(a.assigned_date) - new Date(b.assigned_date));

              sortedTasks.forEach(task => {
                  const tNodeId = `task-${task.id}`;
                  const colors = getStatusColor(task.status);
                  const isOverdue = task.is_overdue;
                  
                  // Task Placement Logic
                  let placementDate;
                  if (['completed', 'review', 'approved'].includes(task.status)) {
                      placementDate = task.completed_at || task.due_date || task.assigned_date;
                  } else {
                      placementDate = new Date().toISOString(); // Current Date
                  }

                  let taskX = dateToX(placementDate);
                  
                  if (taskX < workMinX) workMinX = taskX;
                  if (taskX < clientMinX) clientMinX = taskX;

                  const formatDate = (dateStr) => {
                    if (!dateStr) return 'N/A';
                    const d = new Date(dateStr);
                    if (isNaN(d.getTime())) return 'N/A';
                    return d.toLocaleDateString('en-GB'); 
                  };

                  const taskLabel = (
                    <div style={{ textAlign: 'left', fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ textAlign: 'center', marginBottom: '4px', paddingBottom: '4px', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                        <strong>{task.task_name}</strong>
                        <div>Status: {task.status.replace('_', ' ').toUpperCase()} {isOverdue && task.status !== 'completed' ? '(OVERDUE)' : ''}</div>
                      </div>
                      
                      <div style={{ borderLeft: '3px solid #fbbf24', paddingLeft: '6px', background: 'rgba(0,0,0,0.1)', padding: '2px 2px 2px 6px', borderRadius: '0 4px 4px 0' }}>
                        <span style={{ fontSize: '10px', opacity: 0.8 }}>Assigned:</span><br/>
                        {formatDate(task.assigned_date)}
                      </div>

                      <div style={{ borderLeft: '3px solid #60a5fa', paddingLeft: '6px', background: 'rgba(0,0,0,0.1)', padding: '2px 2px 2px 6px', borderRadius: '0 4px 4px 0' }}>
                        <span style={{ fontSize: '10px', opacity: 0.8 }}>Started:</span><br/>
                        {formatDate(task.started_date)}
                      </div>

                      <div style={{ borderLeft: '3px solid #f87171', paddingLeft: '6px', background: 'rgba(0,0,0,0.1)', padding: '2px 2px 2px 6px', borderRadius: '0 4px 4px 0' }}>
                        <span style={{ fontSize: '10px', opacity: 0.8 }}>Deadline:</span><br/>
                        {formatDate(task.due_date)}
                      </div>

                      <div style={{ borderLeft: '3px solid #34d399', paddingLeft: '6px', background: 'rgba(0,0,0,0.1)', padding: '2px 2px 2px 6px', borderRadius: '0 4px 4px 0' }}>
                        <span style={{ fontSize: '10px', opacity: 0.8 }}>Completed:</span><br/>
                        {formatDate(task.completed_at)}
                      </div>
                    </div>
                  );

                  initialNodes.push({
                    id: tNodeId,
                    data: { label: taskLabel },
                    position: { x: taskX, y: currentY },
                    type: "output",
                    sourcePosition: 'right',
                    targetPosition: 'left',
                    style: {
                      background: isOverdue && task.status !== 'completed' ? "#ef4444" : colors.bg,
                      color: "#fff",
                      border: `1px solid ${isOverdue && task.status !== 'completed' ? '#dc2626' : colors.border}`,
                      width: 200,
                      height: 180,
                      borderRadius: "8px",
                      padding: "8px",
                      fontWeight: "500",
                      fontFamily: "Inter, sans-serif"
                    },
                  });

                  initialEdges.push({
                    id: `e-${wNodeId}-${tNodeId}`,
                    source: wNodeId,
                    target: tNodeId,
                    animated: true,
                    markerEnd: { type: MarkerType.ArrowClosed },
                    style: { stroke: '#94a3b8', strokeWidth: 2 }
                  });

                  // Generate horizontal dashed line to the deadline
                  if (task.due_date) {
                      const dTime = new Date(task.due_date).getTime();
                      if (!isNaN(dTime)) {
                          let deadlineX = dateToX(task.due_date);
                          let startX, lineWidth;
                          
                          if (deadlineX > taskX + 200) {
                              startX = taskX + 200; // start from right edge of node
                              lineWidth = deadlineX - startX;
                          } else if (deadlineX < taskX) {
                              startX = deadlineX; // start from deadline
                              lineWidth = taskX - deadlineX; // end at left edge of node
                          } else {
                              lineWidth = 0; // deadline is inside the node
                          }

                          if (lineWidth > 0) {
                              initialNodes.push({
                                  id: `hline-${task.id}`,
                                  position: { x: startX, y: currentY + 90 }, // vertically centered
                                  data: { label: '' },
                                  style: {
                                      width: lineWidth,
                                      height: 0,
                                      background: 'transparent',
                                      borderTop: '2px dashed #ef4444',
                                      zIndex: -2,
                                      padding: 0
                                  },
                                  draggable: false,
                                  selectable: false
                              });
                          }
                      }
                  }

                  currentY += ROW_HEIGHT;
              });

              let workHeight = currentY - workStartY;
              let workY = workStartY + (workHeight / 2) - 30; // 30 is half of 60px height
              let workX = workMinX - 250;

              initialNodes.push({
                id: wNodeId,
                data: { label: `Work\n${work.workName}` },
                position: { x: workX, y: workY },
                sourcePosition: 'right',
                targetPosition: 'left',
                style: {
                  background: "#3b82f6",
                  color: "#fff",
                  border: "1px solid #2563eb",
                  width: 170,
                  height: 60,
                  borderRadius: "8px",
                  padding: "10px",
                  fontWeight: "bold",
                  textAlign: "center"
                },
              });

              initialEdges.push({
                  id: `e-${cNodeId}-${wNodeId}`,
                  source: cNodeId,
                  target: wNodeId,
                  animated: true,
                  markerEnd: { type: MarkerType.ArrowClosed },
                  style: { stroke: '#64748b', strokeWidth: 2 }
              });
           });

           let clientHeight = currentY - clientStartY;
           let clientY = clientStartY + (clientHeight / 2) - 30;
           let clientX = clientMinX - 500;

           initialNodes.push({
               id: cNodeId,
               data: { label: `Client\n${clientName}` },
               position: { x: clientX, y: clientY },
               sourcePosition: 'right',
               targetPosition: 'left',
               type: "input",
               style: {
                   background: "#475569",
                   color: "#fff",
                   border: "1px solid #334155",
                   width: 180,
                   height: 60,
                   borderRadius: "8px",
                   padding: "10px",
                   fontWeight: "bold",
                   textAlign: "center"
               }
           });
        });

        // 4. Update Grid Lines Height to match actual layout height
        const totalHeight = currentY > 300 ? currentY + 100 : 800;
        initialNodes.forEach(node => {
           if (node.id.startsWith('grid-')) {
               node.style.height = totalHeight;
           }
        });

        setNodes(initialNodes);
        setEdges(initialEdges);
        setLoading(false);
      } catch (err) {
        console.error("Failed to load timeline", err);
        setLoading(false);
      }
    };

    if (id && auth?.token) {
      fetchEmployeeTasks();
    }
  }, [id, auth, financialYear, month, taskStatus]);

  return (
    <Box sx={{ width: '100%', height: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column', p: 2 }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: '#fff', borderRadius: '8px', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button 
            startIcon={<BsArrowLeft />} 
            onClick={() => navigate(-1)}
            sx={{ mr: 2, color: '#475569', textTransform: 'none', fontWeight: 600 }}
            >
            Back
            </Button>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Timeline Route Map: {employeeName || `Employee ${id}`}
            </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <select
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#ffffff',
              fontSize: '14px', outline: 'none', cursor: 'pointer', minWidth: '150px'
            }}
          >
            <option value="">All Years</option>
            {getFinancialYears().map(y => <option key={y} value={y}>{y}</option>)}
          </select>

          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#ffffff',
              fontSize: '14px', outline: 'none', cursor: 'pointer', minWidth: '130px'
            }}
          >
            <option value="">All Months</option>
            <option value="1">January</option>
            <option value="2">February</option>
            <option value="3">March</option>
            <option value="4">April</option>
            <option value="5">May</option>
            <option value="6">June</option>
            <option value="7">July</option>
            <option value="8">August</option>
            <option value="9">September</option>
            <option value="10">October</option>
            <option value="11">November</option>
            <option value="12">December</option>
          </select>

          <select
            value={taskStatus}
            onChange={(e) => setTaskStatus(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#ffffff',
              fontSize: '14px', outline: 'none', cursor: 'pointer', minWidth: '130px'
            }}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="review">Review</option>
            <option value="approved">Approved</option>
            <option value="completed">Completed</option>
            <option value="blocked">Blocked</option>
            <option value="rejected">Rejected</option>
          </select>
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        ) : nodes.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#94a3b8' }}>
                <Typography>No tasks found for the selected filters.</Typography>
            </Box>
        ) : (
            <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            >
            <MiniMap 
                nodeColor={(n) => n.style?.background || '#cbd5e1'}
                maskColor="rgba(248, 250, 252, 0.7)"
            />
            <Controls />
            <Background color="#cbd5e1" gap={16} />
            </ReactFlow>
        )}
      </Box>
    </Box>
  );
}
